import { defineComponent as _defineComponent } from 'vue'
import { ref, watch } from 'vue'
import { computed } from '@vue/reactivity';

import vtextfield from "@busy-human/vue-component-library/components/text-field.vue"
import VCheckbox from '@/components/checkmarkbox.vue'
import VRadioset from '@busy-human/vue-component-library/components/radioset.vue'
import VCodeInput from "../../components/code-input.vue"
import VPhoneInput from "@/components/phone-input.vue";
import Loader from '@/components/loader.vue'

import { Userspaces, Meetings, InviteLinks } from '@busy-human/opt-library'
import { signup, sendVerification, emailLogin, phoneLogin, getUserId } from '@/util/auth';

import {isDevEnv} from "../../util/aux-functions";

type Props = {
    loading: Boolean
}
type Emits = {
    (e: 'login'): void
    (e: 'update:loading', value: boolean): void

    (e: 'previous'): void
    (e: 'progress'): void
}
enum LoginStage {
    VERIFICATION = 1,
    SIGNUP = 3,
    CODE = 4,
}


export default /*#__PURE__*/_defineComponent({
  __name: 'mini-auth',
  props: {
    loading: null
  },
  emits: ["login", "update:loading", "previous", "progress"],
  setup(__props: any, { expose, emit }: { emit: ({
    (e: 'login'): void
    (e: 'update:loading', value: boolean): void

    (e: 'previous'): void
    (e: 'progress'): void
}), expose: any, slots: any, attrs: any }) {
  expose();

const props = __props as {
    loading: Boolean
}





const noScroll = ref<boolean>(false)

const env = ref(ENVIRONMENT);
const isDevEnvironment = ref(false);
isDevEnvironment.value = isDevEnv(window);
const DEV_code = ref<string | null>(null);

const errorMsg = ref<null | string>(null);

const userExists = ref(false)
let enteredCode:string|null = null

let email = ref<string | null>(null)
let phone = ref<string | null>(null)
let firstName = ref<string | null>(null)
let lastName = ref<string | null>(null)
let acceptedTerms = ref(false)

//State Controllers
const verificationSelection = ref<"phone" | "email">("phone")
const stage = ref(LoginStage.VERIFICATION)

const createAccount = async () => {
    if(!validate()) return

    emit('update:loading', true)
    try {
        if(!acceptedTerms.value)
            throw new Error("Please Agree to the Terms and Conditions")
        await signup(firstName.value!, lastName.value!, email.value!, phone.value!, true)
        if(enteredCode != null){
            if(verificationSelection.value === 'email'){
                await emailLogin(email.value!, enteredCode);
            }
            else if(verificationSelection.value === 'phone'){
                await phoneLogin(phone.value!, enteredCode);
            }

            await finishLogin()
        }
    } catch (e) {
        if(e instanceof Error) {
            errorMsg.value = e.message;
        } else {
            errorMsg.value = "Error trying to create account"
        }
        console.error(e);
    }
    
    emit('update:loading', false)
}

const doSendCode = async () => {
    errorMsg.value = null;
    if(!validate()) return;
    const phoneNumber = phone.value;

    emit('update:loading', true)
    
    try {
        const verificationString = verificationSelection.value === 'email'
            ? email.value
            : phoneNumber;

        let result = await sendVerification(verificationSelection.value, verificationString!);
        DEV_code.value = result.data.code || null
        userExists.value = result.data.exists
        stage.value = LoginStage.CODE
    } catch(e) {
        if((e && typeof e === 'object' && e!.toString().includes('Cannot find this user'))){
            stage.value = LoginStage.SIGNUP
        }else{
            console.error(e);
            errorMsg.value = "Error trying to send verification code, try a different method.";
        }
    }
    emit('update:loading', false)
}

const inputCode = ref('');
watch(() => inputCode.value, val => {
    if(val.length === 5) {
        doCodeSubmission(val);
    }
})
const doCodeSubmission = async (code: string) => {
    emit('update:loading', true)
    try {
        if(!userExists.value){
            enteredCode = code
            stage.value = LoginStage.SIGNUP 
        }else{
            if(verificationSelection.value == 'email') {
                await emailLogin(email.value!, code);
            } else if (verificationSelection.value == 'phone') {
                await phoneLogin(phone.value!, code);
            } else {
                console.warn("Somehow trying to login without a code destination")
            }
            errorMsg.value = ""
            await finishLogin()
        }
    } catch (e) {
        console.error(e);
        if(e instanceof Error) {
            errorMsg.value = e.message;
        } else {
            errorMsg.value = "Error logging in";
        }
    }

    emit('update:loading', false)
}

const finishLogin = async () => {
    emit('login')
    emit('update:loading', false)
    emit('progress');
}

//Validation
const emailRegex = /(?:[a-z0-9!#$%&'*+\/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+\/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/
const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im

const validate = (focus?:string) => {
    errorMsg.value = ""

    if(!focus || focus == 'email')
        if((stage.value == LoginStage.SIGNUP || verificationSelection.value === 'email') && (!email.value || !emailRegex.test(email.value.toLowerCase()))){
            errorMsg.value = "Input a Valid Email Address"
            return false;
        }

    if(!focus || focus == 'phone')
        if((stage.value == LoginStage.SIGNUP || verificationSelection.value === 'phone') && (!phone.value || !phoneRegex.test(phone.value.toLowerCase()))){
            errorMsg.value = 'Input a Valid Phone Number'
            return false;
        }

    if(!focus && stage.value == LoginStage.SIGNUP){
        if(!firstName.value){
            errorMsg.value = 'Input your first name'
            return false;
        }

        if(!lastName.value){
            errorMsg.value = 'Input your last name'
            return false;
        }
    }
    return true;
}

watch([email], () => {
    if(errorMsg.value)
        validate('email');
})

watch([phone], () => {
    if(errorMsg.value)
        validate('phone');
})

watch([verificationSelection], () => {
    if(errorMsg.value)
        validate(verificationSelection.value)
})



const previous = () => {
    emit('previous');
}

const proceed = () => {
    emit('progress');
}


const __returned__ = { props, emit, noScroll, env, isDevEnvironment, DEV_code, errorMsg, userExists, enteredCode, email, phone, firstName, lastName, acceptedTerms, LoginStage, verificationSelection, stage, createAccount, doSendCode, inputCode, doCodeSubmission, finishLogin, emailRegex, phoneRegex, validate, previous, proceed, ref, watch, computed, vtextfield, VCheckbox, VRadioset, VCodeInput, VPhoneInput, Loader, Userspaces, Meetings, InviteLinks, signup, sendVerification, emailLogin, phoneLogin, getUserId, isDevEnv }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})